import { defaultChannelsStore } from '../store/defaultChannelsStore';

export type LivePerformanceUwDw = 'UPWIND' | 'DOWNWIND' | string;

export interface LivePerformanceAggregateRow {
  event_id?: number | string;
  Datetime?: string | Date;
  datetime?: string | Date;
  Twa_deg_avg?: number;
  twa_deg_avg?: number;
  [key: string]: unknown;
}

export function passesLivePerformanceTwaGate(
  point: LivePerformanceAggregateRow,
  uwDw: LivePerformanceUwDw
): boolean {
  const mode = String(uwDw || 'UPWIND').toUpperCase();
  const twaAvg = point.Twa_deg_avg ?? point.twa_deg_avg;
  let twaValue: number | null = null;

  if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
    twaValue = Number(twaAvg);
  } else {
    const twaField = defaultChannelsStore.twaName();
    const raw = point[twaField] ?? point.twa ?? point.Twa_deg ?? point.twa_deg;
    if (raw !== undefined && raw !== null && !isNaN(Number(raw))) {
      twaValue = Number(raw);
    }
  }

  if (twaValue === null) {
    return false;
  }

  const absTwa = Math.abs(twaValue);
  if (mode === 'UPWIND') {
    return absTwa < 90;
  }
  if (mode === 'DOWNWIND') {
    return absTwa >= 90;
  }
  return true;
}

function rowDatetimeMs(row: LivePerformanceAggregateRow): number {
  const raw = row.Datetime ?? row.datetime;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function mergeAggregateRows<T extends LivePerformanceAggregateRow>(
  existing: T[],
  incoming: T[]
): T[] {
  if (!incoming.length) return existing;
  const byEventId = new Map<number, T>();
  for (const row of existing) {
    const id = Number(row.event_id);
    if (Number.isInteger(id) && id > 0) {
      byEventId.set(id, row);
    }
  }
  for (const row of incoming) {
    const id = Number(row.event_id);
    if (Number.isInteger(id) && id > 0) {
      byEventId.set(id, row);
    }
  }
  return Array.from(byEventId.values()).sort(
    (a, b) => rowDatetimeMs(a) - rowDatetimeMs(b)
  );
}

export function collectEventIds(rows: LivePerformanceAggregateRow[]): number[] {
  const ids: number[] = [];
  for (const row of rows) {
    const id = Number(row.event_id);
    if (Number.isInteger(id) && id > 0) {
      ids.push(id);
    }
  }
  return ids;
}

export function inferLivePerformanceUwDwFromPoint(
  point: LivePerformanceAggregateRow | null | undefined
): 'UPWIND' | 'DOWNWIND' | null {
  if (!point) return null;

  const twaAvg = point.Twa_deg_avg ?? point.twa_deg_avg;
  let twaValue: number | null = null;

  if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
    twaValue = Number(twaAvg);
  } else {
    const twaField = defaultChannelsStore.twaName();
    const raw = point[twaField] ?? point.twa ?? point.Twa_deg ?? point.twa_deg;
    if (raw !== undefined && raw !== null && !isNaN(Number(raw))) {
      twaValue = Number(raw);
    }
  }

  if (twaValue === null) return null;
  return Math.abs(twaValue) < 90 ? 'UPWIND' : 'DOWNWIND';
}

export function inferLivePerformanceUwDwFromLatestRows(
  rows: LivePerformanceAggregateRow[]
): 'UPWIND' | 'DOWNWIND' | null {
  if (!rows.length) return null;
  const latestId = latestEventIdByDatetime(rows);
  if (latestId == null) return null;
  const latestRow = rows.find((row) => Number(row.event_id) === latestId);
  return inferLivePerformanceUwDwFromPoint(latestRow);
}

export function latestEventIdByDatetime(
  rows: LivePerformanceAggregateRow[]
): number | null {
  if (!rows.length) return null;
  let best: LivePerformanceAggregateRow | null = null;
  let bestMs = -1;
  for (const row of rows) {
    const ms = rowDatetimeMs(row);
    if (ms >= bestMs) {
      bestMs = ms;
      best = row;
    }
  }
  const id = Number(best?.event_id);
  return Number.isInteger(id) && id > 0 ? id : null;
}
